"use client";

/**
 * CookieConsent — GDPR cookie/consent notice.
 *
 * Breakin' Bread uses essential cookies (sign-in/session) which don't require
 * consent, and first-party operational analytics. This banner records the
 * user's choice for any NON-essential use and exposes it globally so future
 * analytics/marketing can check `window.__bbConsent` (or localStorage) before
 * loading. Stored once; never shown again after a choice is made.
 *
 * A slim, non-blocking bar (Gordon 2026-10-29: nothing to close before the
 * first play) — the page stays fully usable while it is up. Copy lives in
 * `common.cookie*` in every locale.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

const KEY = "bb-cookie-consent"; // 'accepted' | 'rejected'

export function getCookieConsent(): "accepted" | "rejected" | null {
  if (typeof window === "undefined") return null;
  try { return (localStorage.getItem(KEY) as any) || null; } catch { return null; }
}

export function CookieConsent() {
  const t = useTranslations("common");
  const pathname = usePathname();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (existing) { (window as any).__bbConsent = existing; return; }
    setShow(true);
  }, []);

  const choose = (val: "accepted" | "rejected") => {
    try { localStorage.setItem(KEY, val); } catch { /* ignore */ }
    (window as any).__bbConsent = val;
    window.dispatchEvent(new CustomEvent("bb:cookie-consent", { detail: val }));
    setShow(false);
  };

  // Not on the unattended event screens (/screens): nobody is there to answer.
  if (!show || pathname?.startsWith("/screens")) return null;

  return (
    <div
      role="region"
      aria-label={t("cookieTitle")}
      /* lifted above the mobile player bar + VU strip + chat FAB zone (written-in-stone: FAB always visible) */
      className="fixed bottom-[80px] sm:bottom-[var(--player-height)] left-0 right-0 z-[120] px-3 pb-2 sm:p-3 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto flex max-w-3xl flex-col gap-2 rounded-xl border border-border bg-background/95 px-3 py-2 shadow-lg backdrop-blur-md sm:flex-row sm:items-center sm:gap-4 sm:px-4">
        <p className="flex-1 text-xs leading-snug text-muted-foreground">
          <span className="font-medium text-foreground">{t("cookieTitle")}.</span>{" "}
          {t("cookieText")}{" "}
          <Link href="/privacy" className="underline hover:text-foreground">{t("cookiePrivacy")}</Link>
        </p>
        <div className="flex shrink-0 gap-2 self-end sm:self-auto">
          <button
            onClick={() => choose("rejected")}
            className="h-8 rounded-lg border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            {t("cookieEssential")}
          </button>
          <button
            onClick={() => choose("accepted")}
            className="h-8 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground hover:opacity-90"
          >
            {t("cookieAccept")}
          </button>
        </div>
      </div>
    </div>
  );
}
